import { supabase } from '../lib/supabase';

export const systemService = {
  // Get all system settings
  async getSystemSettings() {
    try {
      const { data, error } = await supabase
        ?.from('system_settings')
        ?.select('*')
        ?.order('setting_key');
      
      if (error) {
        return { settings: {}, error: error?.message };
      }
      
      // Convert array to object for easier access
      const settingsObject = {};
      data?.forEach(setting => {
        settingsObject[setting?.setting_key] = setting?.setting_value;
      });
      
      return { settings: settingsObject, error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          settings: {}, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { settings: {}, error: 'Failed to load system settings.' };
    }
  },

  // Get specific setting by key
  async getSetting(key) {
    try {
      const { data, error } = await supabase
        ?.from('system_settings')
        ?.select('*')
        ?.eq('setting_key', key)
        ?.single();
      
      if (error) {
        return { setting: null, error: error?.message };
      }
      
      return { setting: data?.setting_value, error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          setting: null, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { setting: null, error: 'Failed to load setting.' };
    }
  },

  // Update system setting
  async updateSetting(key, value, description = null) {
    try {
      const { data, error } = await supabase
        ?.from('system_settings')
        ?.upsert({
          setting_key: key,
          setting_value: value,
          description: description,
          updated_by: (await supabase?.auth?.getUser())?.data?.user?.id
        })
        ?.select()
        ?.single();
      
      if (error) {
        return { setting: null, error: error?.message };
      }
      
      return { setting: data, error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          setting: null, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { setting: null, error: 'Failed to update setting.' };
    }
  },

  // Delete system setting
  async deleteSetting(key) {
    try {
      const { error } = await supabase
        ?.from('system_settings')
        ?.delete()
        ?.eq('setting_key', key);
      
      if (error) {
        return { error: error?.message };
      }
      
      return { error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { error: 'Failed to delete setting.' };
    }
  },

  // Get audit trail for quotes
  async getQuoteAuditTrail(quoteId) {
    try {
      const { data, error } = await supabase
        ?.from('quote_audit_trail')
        ?.select(`
          *,
          performed_by:user_profiles(full_name, email)
        `)
        ?.eq('quote_id', quoteId)
        ?.order('performed_at', { ascending: false });
      
      if (error) {
        return { auditTrail: [], error: error?.message };
      }
      
      return { auditTrail: data || [], error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          auditTrail: [], 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { auditTrail: [], error: 'Failed to load audit trail.' };
    }
  }
};

export default systemService;